export type CameraFacing = 'user' | 'environment' | 'unknown';

export interface CameraInfo {
  deviceId: string;
  label: string;
  facing: CameraFacing;
}

export interface CameraSelection {
  cameras: CameraInfo[];
  activeId: string | null;
  canSwitch: boolean;
  nextId: string | null;
}

/** Device labels are the only portable signal for front/back cameras. */
export function facingFromLabel(label: string): CameraFacing {
  const value = label.toLowerCase();
  if (/back|rear|environment|world/.test(value)) return 'environment';
  if (/front|face|user|selfie/.test(value)) return 'user';
  return 'unknown';
}

export function toCameraInfo(device: { deviceId: string; label: string }): CameraInfo {
  return { deviceId: device.deviceId, label: device.label, facing: facingFromLabel(device.label) };
}

/**
 * Picks the next camera, preferring the opposite facing when labels say so.
 * With a single camera there is nothing to switch to, so `nextId` stays null.
 */
export function selectCameras(
  devices: Array<{ deviceId: string; label: string }>,
  activeId?: string | null,
): CameraSelection {
  const cameras = devices.filter((device) => device.deviceId).map(toCameraInfo);
  if (cameras.length <= 1) {
    return { cameras, activeId: cameras[0]?.deviceId ?? null, canSwitch: false, nextId: null };
  }
  const current = activeId && cameras.some((camera) => camera.deviceId === activeId) ? activeId : cameras[0].deviceId;
  const currentFacing = cameras.find((camera) => camera.deviceId === current)?.facing ?? 'unknown';
  const opposite = cameras.find((camera) => camera.deviceId !== current && camera.facing !== currentFacing);
  const next = opposite ?? cameras[(cameras.findIndex((camera) => camera.deviceId === current) + 1) % cameras.length];
  return { cameras, activeId: current, canSwitch: true, nextId: next.deviceId };
}

export function cameraLabel(camera: CameraInfo | undefined, index: number): string {
  if (!camera) return `Camera ${index + 1}`;
  if (camera.label) return camera.label;
  if (camera.facing === 'environment') return 'Back camera';
  if (camera.facing === 'user') return 'Front camera';
  return `Camera ${index + 1}`;
}
